import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Button, Chip, Divider, MenuItem, Stack, TextField } from '@mui/material';
import type { CellValueChangedEvent, ColDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { usePagination } from '@tiles-erp/hooks';
import { PageContainer, useSaveShortcut } from '@tiles-erp/ui';
import type { BranchPriceItem } from '@tiles-erp/shared-types';
import { DataTable } from '../../components/DataTable';
import { ApiError } from '../../lib/api-client';
import { batchesOf } from '../../lib/batches';
import { ProductFilterBar } from './ProductFilterBar';
import type { ProductFilters } from './api';
import {
  useBranchPrices,
  useBranches,
  useBulkUpdateBranchPrices,
  useCopyBranchPrices,
  useCreateSellingRateColumn,
  useDeleteSellingRateColumn,
  useRenameSellingRateColumn,
  useSellingRateColumns,
} from './branch-prices-api';
import { useSessionBranchId } from '../../lib/session-branch';

interface PriceEdit {
  mrp: number;
  displayPrice: number;
  minSellingPrice: number;
  sellingPrice: number;
  franchiseeRate: number;
  customRates: Record<string, number>;
  version: number;
}

/** Grid row = branch price plus derived margin over landing cost. */
type PriceRow = BranchPriceItem & { marginPct: number | null } & Record<string, unknown>;

type BulkField = 'mrp' | 'minSellingPrice' | 'sellingPrice' | 'franchiseeRate';

const BULK_FIELDS: { value: BulkField; label: string }[] = [
  { value: 'mrp', label: 'MRP' },
  { value: 'minSellingPrice', label: 'Min selling price (incl GST)' },
  { value: 'sellingPrice', label: 'Actual selling price (incl GST)' },
  { value: 'franchiseeRate', label: 'Franchisee rate (incl GST)' },
];

type BulkMode = 'set' | 'markupOnLanding';

const num = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Branch-wise GST-inclusive selling price editor with a reference franchisee rate.
 */
export function SellingPricesPage(): JSX.Element {
  const pagination = usePagination({ initialPageSize: 50 });
  const branches = useBranches();
  const [branchId, setBranchId] = useSessionBranchId();
  const [filters, setFilters] = useState<ProductFilters>({});
  const { data, isFetching, refetch } = useBranchPrices(branchId || undefined, pagination.query, filters);
  const bulkUpdate = useBulkUpdateBranchPrices();
  const rateColumns = useSellingRateColumns();
  const createRateColumn = useCreateSellingRateColumn();
  const renameRateColumn = useRenameSellingRateColumn();
  const deleteRateColumn = useDeleteSellingRateColumn();
  const copyPrices = useCopyBranchPrices();
  const [copyTargetBranchId, setCopyTargetBranchId] = useState('');

  const [edits, setEdits] = useState<Record<string, PriceEdit>>({});
  const [error, setError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const [bulkField, setBulkField] = useState<BulkField>('sellingPrice');
  const [bulkMode, setBulkMode] = useState<BulkMode>('set');
  const [bulkValue, setBulkValue] = useState('');

  const items = data?.items ?? [];

  const effective = (item: BranchPriceItem): PriceEdit =>
    edits[item.productId] ?? {
      mrp: item.mrp ?? 0,
      displayPrice: item.displayPrice ?? 0,
      minSellingPrice: item.minSellingPrice ?? 0,
      sellingPrice: item.sellingPrice ?? 0,
      franchiseeRate: item.franchiseeRate ?? 0,
      customRates: Object.fromEntries(
        (rateColumns.data ?? []).map((column) => [column.id, item.customRates[column.id] ?? 0]),
      ),
      version: item.version,
    };

  const rows = useMemo<PriceRow[]>(
    () =>
      items.map((item) => {
        const current = effective(item);
        const marginPct =
          item.landingCost && item.landingCost > 0 && current.sellingPrice > 0
            ? round2(((current.sellingPrice - item.landingCost) / item.landingCost) * 100)
            : null;
        return {
          ...item,
          ...current,
          ...Object.fromEntries(
            Object.entries(current.customRates).map(([id, rate]) => [`rate_${id}`, rate]),
          ),
          marginPct,
        };
      }),
    [items, edits, rateColumns.data],
  );

  const onCellValueChanged = (event: CellValueChangedEvent<PriceRow>): void => {
    const row = event.data;
    if (!row) return;
    setSavedCount(null);
    setEdits((prev) => ({
      ...prev,
      [row.productId]: {
        mrp: num(row.mrp, 0),
        displayPrice: num(row.displayPrice, 0),
        minSellingPrice: num(row.minSellingPrice, 0),
        sellingPrice: num(row.sellingPrice, 0),
        franchiseeRate: num(row.franchiseeRate, 0),
        customRates: Object.fromEntries(
          (rateColumns.data ?? []).map((column) => [column.id, num(row[`rate_${column.id}`], 0)]),
        ),
        version: prev[row.productId]?.version ?? row.version,
      },
    }));
  };

  const applyToAll = (): void => {
    const value = Number(bulkValue);
    if (!Number.isFinite(value) || value < 0) {
      setError('Enter a valid non-negative number to apply');
      return;
    }
    setError(null);
    setSavedCount(null);
    setEdits((prev) => {
      const next = { ...prev };
      for (const item of items) {
        const current = next[item.productId] ?? effective(item);
        const computed =
          bulkMode === 'set'
            ? value
            : item.landingCost === null
              ? current[bulkField]
              : round2(item.landingCost * (1 + value / 100));
        next[item.productId] = { ...current, [bulkField]: computed };
      }
      return next;
    });
  };

  const dirtyCount = Object.keys(edits).length;

  const save = async (): Promise<void> => {
    setError(null);
    setSavedCount(null);
    setSaving(true);
    try {
      const payload = Object.entries(edits).map(([productId, edit]) => ({
        productId,
        mrp: edit.mrp,
        displayPrice: edit.displayPrice,
        minSellingPrice: edit.minSellingPrice,
        sellingPrice: edit.sellingPrice,
        franchiseeRate: edit.franchiseeRate,
        customRates: edit.customRates,
        version: edit.version,
      }));
      let updatedCount = 0;
      for (const items of batchesOf(payload)) {
        const updated = await bulkUpdate.mutateAsync({ branchId, items });
        updatedCount += updated.length;
      }
      setEdits({});
      setSavedCount(updatedCount);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save prices');
      void refetch();
    } finally {
      setSaving(false);
    }
  };

  const editableCol = (field: BulkField, headerName: string): ColDef<PriceRow> => ({
    field,
    headerName,
    editable: true,
    flex: 0,
    width: 130,
    minWidth: 115,
    valueParser: (p) => num(p.newValue, num(p.oldValue, 0)),
    cellStyle: { backgroundColor: 'rgba(11, 95, 255, 0.06)' },
    valueFormatter: (p) => (p.value === null || p.value === undefined ? '—' : String(p.value)),
  });

  const columns = useMemo<ColDef<PriceRow>[]>(
    () => [
      { field: 'sku', headerName: 'SKU', minWidth: 150 },
      { field: 'name', headerName: 'Name', minWidth: 200 },
      { headerName: 'Rate unit', width: 110, valueGetter: p => p.data?.stockType === 'GRANITE' ? '₹ / sqft' : '₹ / box' },
      { field: 'brandName', headerName: 'Brand', minWidth: 120 },
      { field: 'sizeMm', headerName: 'Size', maxWidth: 110 },
      {
        field: 'landingCost',
        headerName: 'Landing ₹',
        maxWidth: 130,
        valueFormatter: (p) => (p.value === null || p.value === undefined ? '—' : String(p.value)),
      },
      editableCol('mrp', 'MRP ₹'),
      editableCol('sellingPrice', 'Actual selling ₹'),
      editableCol('minSellingPrice', 'Min selling ₹'),
      editableCol('franchiseeRate', 'Franchisee ₹'),
      ...(rateColumns.data ?? []).map((column): ColDef<PriceRow> => ({
        field: `rate_${column.id}`,
        headerName: `${column.name} ₹`,
        editable: true,
        width: 140,
        minWidth: 120,
        valueParser: (p) => num(p.newValue, num(p.oldValue, 0)),
        cellStyle: { backgroundColor: 'rgba(124, 77, 255, 0.08)' },
      })),
      {
        field: 'marginPct',
        headerName: 'Margin %',
        maxWidth: 120,
        valueFormatter: (p) => (p.value === null || p.value === undefined ? '—' : `${p.value}%`),
        cellStyle: (p) => ({
          fontWeight: 600,
          color: typeof p.value === 'number' && p.value < 0 ? '#c62828' : 'inherit',
        }),
      },
    ],
    [rateColumns.data],
  );

  const addRateColumn = async (): Promise<void> => {
    const name = window.prompt('New selling rate column name');
    if (!name?.trim()) return;
    try {
      await createRateColumn.mutateAsync(name.trim());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create rate column');
    }
  };

  const editRateColumn = async (id: string, currentName: string): Promise<void> => {
    const name = window.prompt('Selling rate column name', currentName);
    if (!name?.trim() || name.trim() === currentName) return;
    try {
      await renameRateColumn.mutateAsync({ id, name: name.trim() });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to rename rate column');
    }
  };

  const removeRateColumn = async (id: string, name: string): Promise<void> => {
    if (!window.confirm(`Delete the ${name} rate column and all of its saved values?`)) return;
    try {
      await deleteRateColumn.mutateAsync(id);
      setEdits({});
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete rate column');
    }
  };

  // Ctrl+S saves without reaching for the mouse.
  useSaveShortcut(() => void save());

  return (
    <PageContainer
      title="Selling prices"
      subtitle="Branch-wise selling rates per box, including GST. Franchisee rate is a reference price."
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          {dirtyCount > 0 && <Chip label={`${dirtyCount} unsaved`} color="warning" size="small" />}
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            disabled={dirtyCount === 0}
            onClick={() => {
              setEdits({});
              setError(null);
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={dirtyCount === 0 || saving}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : `Save ${dirtyCount || ''}`}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={1.5}>
        {error && <Alert severity="error">{error}</Alert>}
        {savedCount !== null && (
          <Alert severity="success">Updated prices for {savedCount} products.</Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            select
            label="Branch"
            size="small"
            fullWidth={false}
            value={branchId}
            onChange={(e) => {
              setBranchId(e.target.value);
              setEdits({});
              pagination.setPage(1);
            }}
            sx={{ width: 200 }}
          >
            {(branches.data ?? []).map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                {branch.name}
                {branch.code ? ` (${branch.code})` : ''}
              </MenuItem>
            ))}
          </TextField>
          {branchId && (
            <>
              <Divider orientation="vertical" flexItem />
              <ProductFilterBar
                value={filters}
                showSeries
                showPackageQuantity
                onChange={(next) => {
                  setFilters(next);
                  pagination.setPage(1);
                }}
              />
              <Divider orientation="vertical" flexItem />
              <TextField
                select
                size="small"
                fullWidth={false}
                label="Bulk set"
                value={bulkField}
                onChange={(e) => setBulkField(e.target.value as BulkField)}
                sx={{ width: 160 }}
              >
                {BULK_FIELDS.map((field) => (
                  <MenuItem key={field.value} value={field.value}>
                    {field.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                fullWidth={false}
                label="Mode"
                value={bulkMode}
                onChange={(e) => setBulkMode(e.target.value as BulkMode)}
                sx={{ width: 130 }}
              >
                <MenuItem value="set">Fixed ₹</MenuItem>
                <MenuItem value="markupOnLanding">Markup %</MenuItem>
              </TextField>
              <TextField
                size="small"
                type="number"
                fullWidth={false}
                label={bulkMode === 'set' ? '₹' : '%'}
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
                sx={{ width: 100 }}
              />
              <Button
                variant="outlined"
                startIcon={<PlaylistAddCheckIcon />}
                onClick={applyToAll}
                disabled={bulkValue === '' || items.length === 0}
              >
                Apply ({items.length})
              </Button>
            </>
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => void addRateColumn()}>
            Add rate column
          </Button>
          {(rateColumns.data ?? []).map((column) => (
            <Chip
              key={column.id}
              label={column.name}
              onClick={() => void editRateColumn(column.id, column.name)}
              onDelete={() => void removeRateColumn(column.id, column.name)}
              deleteIcon={<DeleteIcon />}
              icon={<EditIcon />}
              variant="outlined"
            />
          ))}
          {branchId && (
            <>
              <Divider orientation="vertical" flexItem />
              <TextField
                select
                label="Copy prices to"
                size="small"
                value={copyTargetBranchId}
                onChange={(event) => setCopyTargetBranchId(event.target.value)}
                sx={{ width: 220 }}
              >
                {(branches.data ?? []).filter((branch) => branch.id !== branchId).map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={!copyTargetBranchId || copyPrices.isPending}
                onClick={() => {
                  const target = (branches.data ?? []).find((item) => item.id === copyTargetBranchId);
                  if (!window.confirm(`Replace all existing selling rates in ${target?.name ?? 'the target branch'} with rates from the selected branch?`)) return;
                  void copyPrices.mutateAsync({ sourceBranchId: branchId, targetBranchId: copyTargetBranchId })
                    .then((result) => setSavedCount(result.copied))
                    .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Failed to copy prices'));
                }}
              >
                Copy branch prices
              </Button>
            </>
          )}
        </Stack>

        {!branchId ? (
          <Alert severity="info">Select a branch to view and edit its selling prices.</Alert>
        ) : (
          <>

            <DataTable
              rows={rows}
              columns={columns}
              meta={data?.meta}
              pagination={pagination}
              loading={isFetching}
              searchPlaceholder="Search by name, SKU…"
              height={600}
              gridOptions={{
                getRowId: (p) => p.data.productId,
                onCellValueChanged,
                stopEditingWhenCellsLoseFocus: true,
                singleClickEdit: true,
              }}
            />
          </>
        )}
      </Stack>
    </PageContainer>
  );
}
